import { useSelector } from "react-redux";

import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { ContactWidgetHeader } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/ContactWidget/ContactWidgetHeader";
import { StyledContactWidget } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/ContactWidget/styles";
import useAgentContactReveal from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/hooks/useAgentContactReveal";
import { useInvalidateContacts } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/hooks/useInvalidateContacts";
import type { ContactWidgetData } from "app/pages/sales-intelligence/modules/ai-agent/types/superAgent";
import { useContactsQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useContactsQuota";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import {
    useIsContactNotRevealed,
    useIsContactBeingRevealed,
} from "app/pages/sales-intelligence/sub-modules/common/hooks/useContactRevealStatus";
import ContactDetailsCell from "app/pages/sales-intelligence/sub-modules/contacts-search/components/cells/ContactDetailsCell/ContactDetailsCell";
import { selectContactsCost } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import type {
    ContactEntryType,
    ContactSearchEntry,
    CRMEntity,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import { selectIsContactsReadonly } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

type Props = {
    contact: ContactSearchEntry;
    widgetData: ContactWidgetData;
    onExportToCRM: (contact: ContactSearchEntry, destination: Integration, as?: CRMEntity) => void;
};

export const ContactWidgetItem = ({ contact, onExportToCRM, widgetData }: Props) => {
    const isContactsAvailabilityReadonly = useSelector(selectIsContactsReadonly);

    const contactsCost = useSelector(selectContactsCost);
    const isContactIdNotRevealed = useIsContactNotRevealed(contact.id);
    const isContactBeingRevealed = useIsContactBeingRevealed(contact.id);
    const invalidateContactsQuery = useInvalidateContacts(widgetData);

    const { data: contactsQuota, isQuotaReached } = useContactsQuota();

    const { reveal, isLoading: isRevealLoading } = useAgentContactReveal(contact, widgetData);

    const isLoading = isRevealLoading || isContactBeingRevealed;
    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;

    const refetchAfterTermsApproval = async () => {
        await invalidateContactsQuery();
    };

    const handleContactReveal = () => {
        if (isQuotaReached && !contact.isRevealed) {
            return;
        }

        AIAgentTrackingService.superAgent.trackContactWidgetReveal("card");
        reveal();
    };

    const handleContactEntryCopy = (entryType: ContactEntryType) => {
        AIAgentTrackingService.superAgent.trackContactEntryCopy(entryType);
    };

    return (
        <StyledContactWidget data-automation="contact-widget">
            <ContactWidgetHeader
                contact={contact}
                onExportToCRM={onExportToCRM}
            />
            <ContactDetailsCell
                isLoading={isLoading}
                isContactsAvailabilityReadonly={isContactsAvailabilityReadonly}
                isActionPanelActivated={false}
                isDataCreditsEnabled={isDataCreditsEnabled}
                remainingRevealQuota={contactsQuota?.remaining}
                contactsCost={contactsCost}
                isContactIdNotRevealed={isContactIdNotRevealed}
                rowData={contact}
                onReveal={handleContactReveal}
                onApprove={refetchAfterTermsApproval}
                onCopy={handleContactEntryCopy}
                dataAutomationPrefix="contact-widget"
            />
        </StyledContactWidget>
    );
};
